import { useEffect, useState, useCallback, useMemo } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate, Link, useLocation } from "react-router-dom";
import { 
  getSentProfileViewRequestsAction, 
  clearSentProfileViewRequestsAction
} from "../redux/actions/userActions";
import CommonLoading from "../components/loader/CommonLoading";
import ContentWithCards from "../components/shared/ContentWithCards";
import LoadMoreButton from "../components/shared/LoadMoreButton";
import { FaPaperPlane } from "react-icons/fa";
import CountdownTimer from "../components/shared/CountdownTimer";

const SentProfileViewRequests = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(true);
  const [isLoadMoreLoading, setIsLoadMoreLoading] = useState(false);
  const sortBy = "requestTime"; // Fixed sort by request time
  const sortOrder = "desc"; // Fixed sort order (newest first)
  
  const sentRequests = useSelector((state) => state.user?.sentProfileViewRequests || []);
  const totalSentRequests = useSelector((state) => state.user?.totalSentProfileViewRequests || 0);

  const LIMIT = 25;

  useEffect(() => {
    const fetchRequests = async () => {
      setLoading(true);
      await dispatch(getSentProfileViewRequestsAction(null, LIMIT, 0));
      setLoading(false);
    };

    fetchRequests();

    return () => {
      dispatch(clearSentProfileViewRequestsAction());
    };
  }, [dispatch, LIMIT]);

  const handleUserClick = useCallback((user) => {
    const profileUrl = user?.profileUrl || user?._id;
    if (profileUrl) {
      navigate(`/profile/${profileUrl}`);
    }
  }, [navigate]);

  const handleLoadMore = useCallback(() => {
    setIsLoadMoreLoading(true);
    const currentLength = sentRequests?.length || 0;
    dispatch(getSentProfileViewRequestsAction(null, LIMIT, currentLength)).finally(() => {
      setIsLoadMoreLoading(false);
    });
  }, [dispatch, LIMIT, sentRequests?.length]);

  // Helper function to calculate expiry date
  const calculateExpiryDate = useCallback((acceptedAt, expiryValue, expiryUnit) => {
    if (!acceptedAt || !expiryValue || !expiryUnit) return null;
    const acceptedDate = new Date(acceptedAt);
    let milliseconds = 0;
    switch (expiryUnit) {
      case "hours": milliseconds = expiryValue * 60 * 60 * 1000; break;
      case "days": milliseconds = expiryValue * 24 * 60 * 60 * 1000; break;
      case "weeks": milliseconds = expiryValue * 7 * 24 * 60 * 60 * 1000; break;
      case "years": milliseconds = expiryValue * 365 * 24 * 60 * 60 * 1000; break;
      default: return null;
    }
    return new Date(acceptedDate.getTime() + milliseconds);
  }, []);

  // Sort requests (backend already returns only latest per user)
  const sortedRequests = useMemo(() => {
    if (!sentRequests || sentRequests.length === 0) return [];
    
    const sorted = [...sentRequests].sort((a, b) => {
      if (sortBy === "requestTime") {
        const dateA = new Date(a.createdAt).getTime();
        const dateB = new Date(b.createdAt).getTime();
        return sortOrder === "asc" ? dateA - dateB : dateB - dateA;
      } else if (sortBy === "expiryTime") {
        // Only sort by expiry if both have accepted status and expiry settings
        const expiryA = a.status === "accepted" && 
          a.profileOwner?.privacySettings?.accessType === "expiry" &&
          a.profileOwner?.privacySettings?.expiryValue &&
          a.profileOwner?.privacySettings?.expiryUnit
          ? calculateExpiryDate(a.updatedAt, a.profileOwner.privacySettings.expiryValue, a.profileOwner.privacySettings.expiryUnit)
          : null;
        const expiryB = b.status === "accepted" && 
          b.profileOwner?.privacySettings?.accessType === "expiry" &&
          b.profileOwner?.privacySettings?.expiryValue &&
          b.profileOwner?.privacySettings?.expiryUnit
          ? calculateExpiryDate(b.updatedAt, b.profileOwner.privacySettings.expiryValue, b.profileOwner.privacySettings.expiryUnit)
          : null;
        
        // Put requests without expiry at the end
        if (!expiryA && !expiryB) return 0;
        if (!expiryA) return 1;
        if (!expiryB) return -1;
        
        const timeA = expiryA.getTime();
        const timeB = expiryB.getTime();
        return sortOrder === "asc" ? timeA - timeB : timeB - timeA;
      }
      return 0;
    });
    
    return sorted;
  }, [sentRequests, sortBy, sortOrder, calculateExpiryDate]);


  const displayedRequests = useMemo(() => {
    return sortedRequests.map((request) => {
      const profileOwner = request.profileOwner;
      if (!profileOwner) return null;

      return (
        <div 
          key={request._id}
          className="bg-white border rounded-md w-full shadow-2xl shadow-[#f2f5fc] relative hover:shadow-lg hover:border-primary-300 transition-all duration-300"
        >
          <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-4">
            <div className="hover:bg-gray-50 rounded-md p-3 -m-3 transition-colors">
              <div className="flex items-start justify-between gap-3">
                {/* Left: User info with times below name */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-start gap-2">
                    <img
                      src={profileOwner.avatar || "/default-avatar.png"}
                      alt="Avatar"
                      className="w-10 h-10 rounded-full object-cover flex-shrink-0 mt-0.5"
                    />
                    <div className="flex-1 min-w-0">
                      <div 
                        className="cursor-pointer mb-1"
                        onClick={() => handleUserClick(profileOwner)}
                      >
                        <h4 className="font-semibold text-gray-800 font-heading text-base truncate">
                          {profileOwner.name}
                        </h4>
                      </div>
                      
                      {/* Times directly below name - aligned */}
                      <div className="flex flex-col gap-0.5">
                        <span className="text-xs text-gray-600 font-medium font-flex-regular">
                          Sent: {new Date(request.createdAt).toLocaleString("en-US", {
                            year: "numeric",
                            month: "long",
                            day: "numeric",
                            hour: "2-digit",
                            minute: "2-digit",
                          })}
                        </span>
                        {request.status === "accepted" &&
                          profileOwner?.privacySettings?.accessType === "expiry" &&
                          profileOwner?.privacySettings?.expiryValue &&
                          profileOwner?.privacySettings?.expiryUnit &&
                          request.updatedAt && (() => {
                            // Calculate expiry date
                            const acceptedDate = new Date(request.updatedAt);
                            let milliseconds = 0;
                            switch (profileOwner.privacySettings.expiryUnit) {
                              case "hours": milliseconds = profileOwner.privacySettings.expiryValue * 60 * 60 * 1000; break;
                              case "days": milliseconds = profileOwner.privacySettings.expiryValue * 24 * 60 * 60 * 1000; break;
                              case "weeks": milliseconds = profileOwner.privacySettings.expiryValue * 7 * 24 * 60 * 60 * 1000; break;
                              case "years": milliseconds = profileOwner.privacySettings.expiryValue * 365 * 24 * 60 * 60 * 1000; break;
                              default: milliseconds = 0; break;
                            }
                            const expiryDate = new Date(acceptedDate.getTime() + milliseconds);
                            const isExpired = new Date() > expiryDate;
                            
                            return (
                              <span className="text-xs text-gray-600 font-medium font-flex-regular">
                                {isExpired ? "Expired: " : "Expires: "}
                                {expiryDate.toLocaleString("en-US", {
                                  year: "numeric",
                                  month: "long",
                                  day: "numeric",
                                  hour: "2-digit",
                                  minute: "2-digit",
                                })}
                              </span>
                            );
                          })()}
                      </div>
                    </div>
                  </div>
                </div>
                
                {/* Right: Status badges - stacked vertically */}
                <div className="flex-shrink-0">
                  {request.status === "pending" && (
                    <span className="px-3 py-1 text-xs font-semibold text-yellow-700 bg-yellow-100 rounded-full whitespace-nowrap">
                      Pending
                    </span>
                  )}
                  {request.status === "accepted" && (() => {
                    // Check if access has expired
                    let isExpired = false;
                    if (profileOwner?.privacySettings?.accessType === "expiry" &&
                        profileOwner?.privacySettings?.expiryValue &&
                        profileOwner?.privacySettings?.expiryUnit &&
                        request.updatedAt) {
                      const acceptedDate = new Date(request.updatedAt);
                      let milliseconds = 0;
                      switch (profileOwner.privacySettings.expiryUnit) {
                        case "hours": milliseconds = profileOwner.privacySettings.expiryValue * 60 * 60 * 1000; break;
                        case "days": milliseconds = profileOwner.privacySettings.expiryValue * 24 * 60 * 60 * 1000; break;
                        case "weeks": milliseconds = profileOwner.privacySettings.expiryValue * 7 * 24 * 60 * 60 * 1000; break;
                        case "years": milliseconds = profileOwner.privacySettings.expiryValue * 365 * 24 * 60 * 60 * 1000; break;
                        default: milliseconds = 0; break;
                      }
                      const expiryDate = new Date(acceptedDate.getTime() + milliseconds);
                      isExpired = new Date() > expiryDate;
                    }

                    return (
                      <div className="flex flex-col items-end gap-1.5">
                        {/* Top: Accepted badge */}
                        <span className="px-3 py-1 text-xs font-semibold text-green-800 bg-green-200 rounded-full whitespace-nowrap">
                          Accepted
                        </span>
                        {/* Bottom: Expiry countdown */}
                        {profileOwner?.privacySettings?.accessType === "expiry" &&
                          profileOwner?.privacySettings?.expiryValue &&
                          profileOwner?.privacySettings?.expiryUnit &&
                          !isExpired && (
                          <div className="flex items-center justify-end">
                            <CountdownTimer
                              acceptedAt={request.updatedAt}
                              expiryValue={profileOwner.privacySettings.expiryValue}
                              expiryUnit={profileOwner.privacySettings.expiryUnit}
                            />
                          </div>
                        )}
                        {/* Access Expired badge - shown at bottom when expired */}
                        {isExpired && (
                          <span className="px-3 py-1 text-xs font-semibold text-action bg-action/10 rounded-full whitespace-nowrap">
                            Access Expired
                          </span>
                        )}
                      </div>
                    );
                  })()}
                  {request.status === "denied" && (
                    <span className="px-3 py-1 text-xs font-semibold text-action bg-action/10 rounded-full whitespace-nowrap">
                      Denied
                    </span>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      );
    }).filter(Boolean);
  }, [sortedRequests, handleUserClick]);

  const shouldShowLoadMore = useMemo(() => {
    if (!sentRequests || sentRequests.length === 0) return false;
    const currentCount = sentRequests?.length || 0;
    const totalCount = totalSentRequests || 0;
    return currentCount < totalCount && totalCount > LIMIT;
  }, [sentRequests, totalSentRequests, LIMIT]);

  return (
    <ContentWithCards>
      <div className="main-section bg-white border">
        <div className="p-3">
          {loading ? (
            <div className="flex items-center justify-center h-screen">
              <CommonLoading />
            </div>
          ) : (
            <div>
              {/* Title and Tabs */}
              <div className="mb-4 border-b py-2 pl-2 pr-2">
                <div className="flex items-center justify-between">
                  <h2 className="text-lg font-semibold text-gray-700">
                    Sent MyST Profile Requests
                  </h2>
                  
                  {/* Tabs */}
                  <div className="flex gap-2">
                    <Link
                      to="/skills-circle/received-follow-requests"
                      className={`px-4 py-2 text-sm font-medium rounded-t-lg transition-colors ${
                        location.pathname === "/skills-circle/received-follow-requests"
                          ? "bg-primary text-white"
                          : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                      }`}
                    >
                      Follow
                    </Link>
                    <Link
                      to="/skills-circle/received-profile-requests"
                      className={`px-4 py-2 text-sm font-medium rounded-t-lg transition-colors ${
                        location.pathname === "/skills-circle/received-profile-requests"
                          ? "bg-primary text-white"
                          : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                      }`}
                    >
                      MyST Profile
                    </Link>
                  </div>
                </div>
              </div>
              
              {sentRequests?.length > 0 ? (
                <>
                  <div className="grid grid-cols-1 gap-3 items-start px-2">
                    {displayedRequests}
                  </div>
                  
                  {shouldShowLoadMore && (
                    <LoadMoreButton
                      onClick={handleLoadMore}
                      isLoading={isLoadMoreLoading}
                      text={`Load More Requests (${sentRequests?.length || 0}/${totalSentRequests || 0})`}
                    />
                  )}
                </>
              ) : (
                <div className="text-center py-8">
                  <div className="flex justify-center items-center flex-col">
                    <FaPaperPlane className="text-6xl text-gray-300 mb-4" />
                    <p className="text-gray-500 py-4 font-roboto-flex">
                      You haven't sent any profile view requests yet.
                    </p>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </ContentWithCards>
  );
};

export default SentProfileViewRequests;

